'use client';

import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  Alert,
  Modal,
} from '@mui/material';
import {
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { X } from 'lucide-react';
import { IInvoiceViewModel } from 'viewModels';
import { TransactionType, InvoiceStatus } from 'types/enums';
import InvoiceController from 'controllers/InvoiceController';
import { PaymentsController } from 'controllers/PaymentsController';
import { formatCurrency, formatDate } from 'utils';
import {
  InvoiceHeader,
  InvoiceInfo,
  InvoiceDetails,
  TransactionsSection,
  TransactionsHeader,
  TransactionsTable,
} from './styles';

interface InvoiceDetailsModalProps {
  open: boolean;
  onClose: () => void;
  invoiceId: string;
  isAdmin?: boolean;
}

const InvoiceDetailsModal: React.FC<InvoiceDetailsModalProps> = ({
  open,
  onClose,
  invoiceId,
  isAdmin = false,
}) => {
  const [invoice, setInvoice] = useState<IInvoiceViewModel | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedTransactions, setExpandedTransactions] = useState<
    string | null
  >(null);

  const loadInvoice = async () => {
    if (!invoiceId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await InvoiceController.getInvoiceById(invoiceId);

      if (response.success) {
        setInvoice(response.data);
      } else {
        setError('Erro ao carregar fatura');
      }
    } catch (err) {
      setError('Erro ao carregar fatura');
      console.error('Erro ao carregar fatura:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && invoiceId) {
      loadInvoice();
    }
  }, [open, invoiceId]);

  const handleExportPdf = async () => {
    if (!invoice) return;

    try {
      await InvoiceController.exportInvoicePdf(invoice.id);
    } catch (err) {
      console.error('Erro ao exportar PDF:', err);
    }
  };

  const handleExportPdfDetailed = async () => {
    if (!invoice) return;

    try {
      // Usando o endpoint correto do PaymentsController que aceita parâmetro detailed
      await PaymentsController.exportInvoice(
        invoice.userId,
        true, // detailed = true
        typeof invoice.startDate === 'string'
          ? invoice.startDate
          : invoice.startDate.toISOString(),
        typeof invoice.endDate === 'string'
          ? invoice.endDate
          : invoice.endDate.toISOString()
      );
    } catch (err) {
      console.error('Erro ao exportar PDF detalhado:', err);
    }
  };

  const handleMarkAsPaid = async () => {
    if (!invoice) return;

    // Confirmação antes de marcar como paga
    const confirmed = window.confirm(
      'Tem certeza que deseja marcar esta fatura como paga? Esta ação não pode ser desfeita.'
    );

    if (!confirmed) return;

    try {
      const response = await InvoiceController.markInvoiceAsPaid(invoice.id);
      if (response.success) {
        setInvoice(prev =>
          prev ? { ...prev, status: InvoiceStatus.PAID } : null
        );
      }
    } catch (err) {
      console.error('Erro ao marcar como paga:', err);
    }
  };

  const getStatusColor = (status: InvoiceStatus) => {
    switch (status) {
      case InvoiceStatus.OPEN:
        return 'warning';
      case InvoiceStatus.CLOSED:
        return 'info';
      case InvoiceStatus.PAID:
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: InvoiceStatus) => {
    switch (status) {
      case InvoiceStatus.OPEN:
        return 'Aberta';
      case InvoiceStatus.CLOSED:
        return 'Fechada';
      case InvoiceStatus.PAID:
        return 'Paga';
      default:
        return 'Desconhecido';
    }
  };

  const getTransactionTypeText = (type: TransactionType) => {
    switch (type) {
      case TransactionType.CREDIT:
        return 'Crédito';
      case TransactionType.DEBIT:
        return 'Débito';
      default:
        return 'Desconhecido';
    }
  };

  const getTransactionTypeColor = (type: TransactionType) => {
    switch (type) {
      case TransactionType.CREDIT:
        return 'success';
      case TransactionType.DEBIT:
        return 'error';
      default:
        return 'default';
    }
  };

  const toggleTransactionExpansion = (transactionId: string) => {
    setExpandedTransactions(prev => {
      // Se a transação já está expandida, fecha ela
      if (prev === transactionId) {
        return null;
      }
      // Se outra transação está expandida, fecha ela e abre a nova
      return transactionId;
    });
  };

  const isTransactionExpanded = (transactionId: string) => {
    return expandedTransactions === transactionId;
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <Paper
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          borderRadius: 2,
          outline: 'none',
          width: { xs: '98vw', sm: '95vw', md: '90vw', lg: '85vw' },
          height: { xs: '95vh', sm: '90vh' },
          maxWidth: '1400px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: '1400px',
            backgroundColor: 'white',
            borderRadius: '8px',
            overflow: 'hidden',
            margin: '0 auto',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            minHeight: '60vh',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            height: '100%',
          }}
        >
          <Box
            sx={{
              padding: '16px 24px',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: 'white',
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                id="modal-title"
                sx={{
                  margin: 0,
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  color: '#293778',
                }}
              >
                Detalhes da Fatura
              </Typography>
              <Typography
                id="modal-description"
                sx={{
                  margin: '4px 0 0 0',
                  fontSize: '0.875rem',
                  color: '#6b7280',
                }}
              >
                Visualize os detalhes da fatura e suas transações.
              </Typography>
            </Box>
            <Button
              onClick={onClose}
              sx={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '4px',
                color: '#6b7280',
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                },
              }}
            >
              <X size={20} />
            </Button>
          </Box>

          <Box
            sx={{
              flex: 1,
              padding: '24px',
              overflowY: 'auto',
              backgroundColor: 'white',
              minHeight: 0,
            }}
          >
            {loading ? (
              <Box>
                <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
                <Skeleton variant="rectangular" height={400} />
              </Box>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : invoice ? (
              <Box>
                {/* Cabeçalho da Fatura */}
                <InvoiceHeader>
                  <InvoiceInfo>
                    <Box>
                      <Typography variant="h5" gutterBottom>
                        {invoice.invoiceNumber ||
                          `Fatura #${invoice.id.slice(0, 8)}`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Cliente: {invoice.userName} ({invoice.userEmail})
                      </Typography>
                    </Box>
                    <Chip
                      label={getStatusText(invoice.status)}
                      color={getStatusColor(invoice.status) as any}
                      size="medium"
                    />
                  </InvoiceInfo>

                  <InvoiceDetails>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Período: {formatDate(invoice.startDate)} a{' '}
                        {formatDate(invoice.endDate)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Fechada em:{' '}
                        {invoice.closedAt
                          ? formatDate(invoice.closedAt)
                          : 'N/A'}
                      </Typography>
                    </Box>
                    <Typography variant="h4" color="primary">
                      {formatCurrency(invoice.totalAmount)}
                    </Typography>
                  </InvoiceDetails>
                </InvoiceHeader>

                {/* Seção de Transações */}
                <TransactionsSection>
                  <TransactionsHeader>
                    <Typography variant="h6">
                      Transações ({invoice.transactions.length})
                    </Typography>
                  </TransactionsHeader>

                  <TransactionsTable>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Data</TableCell>
                            <TableCell>Tipo</TableCell>
                            <TableCell>Descrição</TableCell>
                            <TableCell align="right">Valor</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {invoice.transactions
                            .sort(
                              (a, b) =>
                                new Date(a.createdAt).getTime() -
                                new Date(b.createdAt).getTime()
                            )
                            .map(transaction => (
                              <React.Fragment key={transaction.id || ''}>
                                <TableRow
                                  sx={{
                                    cursor: 'pointer',
                                    '&:hover': {
                                      backgroundColor: '#f8fafc',
                                    },
                                  }}
                                  onClick={() =>
                                    toggleTransactionExpansion(
                                      transaction.id || ''
                                    )
                                  }
                                >
                                  <TableCell>
                                    {formatDate(transaction.createdAt)}
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={getTransactionTypeText(
                                        transaction.transactionType
                                      )}
                                      color={
                                        getTransactionTypeColor(
                                          transaction.transactionType
                                        ) as any
                                      }
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {transaction.description}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography
                                      color={
                                        transaction.transactionType ===
                                        TransactionType.CREDIT
                                          ? 'success.main'
                                          : 'error.main'
                                      }
                                    >
                                      {formatCurrency(transaction.amount)}
                                    </Typography>
                                  </TableCell>
                                </TableRow>

                                {/* Detalhes expansíveis da transação */}
                                {isTransactionExpanded(
                                  transaction.id || ''
                                ) && (
                                  <TableRow>
                                    <TableCell colSpan={4} sx={{ padding: 0 }}>
                                      <Box
                                        sx={{
                                          backgroundColor: '#f8fafc',
                                          borderTop: '1px solid #e2e8f0',
                                          padding: '16px',
                                        }}
                                      >
                                        <Typography
                                          variant="subtitle2"
                                          gutterBottom
                                        >
                                          Detalhes do Cálculo
                                        </Typography>

                                        {transaction.metadata
                                          ?.calculationDetails ? (
                                          <Box>
                                            {/* Informações gerais do cálculo */}
                                            <Box sx={{ mb: 2 }}>
                                              <Typography
                                                variant="subtitle2"
                                                color="primary"
                                                gutterBottom
                                              >
                                                Informações do Plano
                                              </Typography>
                                              <Box
                                                sx={{
                                                  display: 'grid',
                                                  gridTemplateColumns:
                                                    '1fr 1fr',
                                                  gap: 1,
                                                  mb: 2,
                                                }}
                                              >
                                                <Box>
                                                  <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                      fontSize: '0.75rem',
                                                    }}
                                                  >
                                                    Plano
                                                  </Typography>
                                                  <Typography
                                                    variant="body2"
                                                    fontWeight="medium"
                                                  >
                                                    {
                                                      transaction.metadata
                                                        .calculationDetails
                                                        .planName
                                                    }
                                                  </Typography>
                                                </Box>
                                                <Box>
                                                  <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                      fontSize: '0.75rem',
                                                    }}
                                                  >
                                                    Total Processado
                                                  </Typography>
                                                  <Typography
                                                    variant="body2"
                                                    fontWeight="medium"
                                                  >
                                                    {
                                                      transaction.metadata
                                                        .calculationDetails
                                                        .totalFontsProcessed
                                                    }{' '}
                                                    fontes
                                                  </Typography>
                                                </Box>
                                              </Box>
                                            </Box>

                                            {/* Cálculos das fontes */}
                                            {transaction.metadata
                                              .calculationDetails
                                              .fontCalculations && (
                                              <Box>
                                                <Typography
                                                  variant="subtitle2"
                                                  color="primary"
                                                  gutterBottom
                                                >
                                                  Cálculos por Fonte
                                                </Typography>
                                                {transaction.metadata.calculationDetails.fontCalculations.map(
                                                  (
                                                    fontCalc: any,
                                                    index: number
                                                  ) => (
                                                    <Box
                                                      key={index}
                                                      sx={{
                                                        border:
                                                          '1px solid #e2e8f0',
                                                        borderRadius: '8px',
                                                        p: 2,
                                                        mb: 1,
                                                        backgroundColor:
                                                          'white',
                                                      }}
                                                    >
                                                      {/* Cabeçalho da fonte */}
                                                      <Box
                                                        sx={{
                                                          display: 'flex',
                                                          justifyContent:
                                                            'space-between',
                                                          alignItems: 'center',
                                                          mb: 1,
                                                        }}
                                                      >
                                                        <Typography
                                                          variant="body2"
                                                          fontWeight="bold"
                                                          color="primary"
                                                        >
                                                          {fontCalc.fontName}
                                                        </Typography>
                                                        <Chip
                                                          label={
                                                            fontCalc.calculatedValueFormatted
                                                          }
                                                          color="success"
                                                          size="small"
                                                          variant="outlined"
                                                        />
                                                      </Box>

                                                      {/* Detalhes do cálculo */}
                                                      <Box
                                                        sx={{
                                                          display: 'grid',
                                                          gridTemplateColumns:
                                                            '1fr 1fr',
                                                          gap: 1,
                                                          fontSize: '0.75rem',
                                                        }}
                                                      >
                                                        <Box>
                                                          <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{
                                                              fontSize:
                                                                '0.7rem',
                                                            }}
                                                          >
                                                            Tipo de Cobrança
                                                          </Typography>
                                                          <Typography
                                                            variant="body2"
                                                            sx={{
                                                              fontSize:
                                                                '0.75rem',
                                                            }}
                                                          >
                                                            {fontCalc.factorType ===
                                                            'PER_EXECUTION'
                                                              ? 'Por Execução'
                                                              : fontCalc.factorType ===
                                                                'PER_ITEM'
                                                              ? 'Por Item'
                                                              : fontCalc.factorType ===
                                                                'PER_TIME'
                                                              ? 'Por Tempo'
                                                              : fontCalc.factorType}
                                                          </Typography>
                                                        </Box>
                                                        <Box>
                                                          <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{
                                                              fontSize:
                                                                '0.7rem',
                                                            }}
                                                          >
                                                            Valor Base
                                                          </Typography>
                                                          <Typography
                                                            variant="body2"
                                                            sx={{
                                                              fontSize:
                                                                '0.75rem',
                                                            }}
                                                          >
                                                            {
                                                              fontCalc.baseValueFormatted
                                                            }
                                                          </Typography>
                                                        </Box>
                                                        {fontCalc.itemCount !==
                                                          null && (
                                                          <Box>
                                                            <Typography
                                                              variant="body2"
                                                              color="text.secondary"
                                                              sx={{
                                                                fontSize:
                                                                  '0.7rem',
                                                              }}
                                                            >
                                                              Itens Processados
                                                            </Typography>
                                                            <Typography
                                                              variant="body2"
                                                              sx={{
                                                                fontSize:
                                                                  '0.75rem',
                                                              }}
                                                            >
                                                              {
                                                                fontCalc.itemCount
                                                              }
                                                            </Typography>
                                                          </Box>
                                                        )}
                                                        {fontCalc.executionTimeSeconds && (
                                                          <Box>
                                                            <Typography
                                                              variant="body2"
                                                              color="text.secondary"
                                                              sx={{
                                                                fontSize:
                                                                  '0.7rem',
                                                              }}
                                                            >
                                                              Tempo de Execução
                                                            </Typography>
                                                            <Typography
                                                              variant="body2"
                                                              sx={{
                                                                fontSize:
                                                                  '0.75rem',
                                                              }}
                                                            >
                                                              {fontCalc.executionTimeSeconds.toFixed(
                                                                2
                                                              )}
                                                              s
                                                            </Typography>
                                                          </Box>
                                                        )}
                                                        {fontCalc.executionStartTime && (
                                                          <Box
                                                            sx={{
                                                              gridColumn:
                                                                '1 / -1',
                                                            }}
                                                          >
                                                            <Typography
                                                              variant="body2"
                                                              color="text.secondary"
                                                              sx={{
                                                                fontSize:
                                                                  '0.7rem',
                                                              }}
                                                            >
                                                              Início da Execução
                                                            </Typography>
                                                            <Typography
                                                              variant="body2"
                                                              sx={{
                                                                fontSize:
                                                                  '0.75rem',
                                                              }}
                                                            >
                                                              {formatDate(
                                                                new Date(
                                                                  fontCalc.executionStartTime
                                                                )
                                                              )}
                                                            </Typography>
                                                          </Box>
                                                        )}
                                                      </Box>

                                                      {/* Explicação do cálculo */}
                                                      <Box
                                                        sx={{
                                                          mt: 1,
                                                          pt: 1,
                                                          borderTop:
                                                            '1px solid #f1f5f9',
                                                        }}
                                                      >
                                                        <Typography
                                                          variant="body2"
                                                          color="text.secondary"
                                                          sx={{
                                                            fontSize: '0.75rem',
                                                            fontStyle: 'italic',
                                                          }}
                                                        >
                                                          {
                                                            fontCalc.calculationDetails
                                                          }
                                                        </Typography>
                                                      </Box>
                                                    </Box>
                                                  )
                                                )}
                                              </Box>
                                            )}

                                            {/* Taxas de fontes extras */}
                                            {transaction.metadata
                                              .calculationDetails
                                              .extraFontsBaseValue && (
                                              <Box
                                                sx={{
                                                  mt: 2,
                                                  p: 2,
                                                  backgroundColor: '#fef3c7',
                                                  borderRadius: '8px',
                                                  border: '1px solid #f59e0b',
                                                }}
                                              >
                                                <Typography
                                                  variant="subtitle2"
                                                  color="warning.main"
                                                  gutterBottom
                                                >
                                                  Taxa de Fontes Extras
                                                </Typography>
                                                <Box
                                                  sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns:
                                                      '1fr 1fr',
                                                    gap: 1,
                                                  }}
                                                >
                                                  <Box>
                                                    <Typography
                                                      variant="body2"
                                                      color="text.secondary"
                                                      sx={{
                                                        fontSize: '0.75rem',
                                                      }}
                                                    >
                                                      Valor Base
                                                    </Typography>
                                                    <Typography
                                                      variant="body2"
                                                      fontWeight="medium"
                                                    >
                                                      {
                                                        transaction.metadata
                                                          .calculationDetails
                                                          .extraFontsBaseValueFormatted
                                                      }
                                                    </Typography>
                                                  </Box>
                                                  <Box>
                                                    <Typography
                                                      variant="body2"
                                                      color="text.secondary"
                                                      sx={{
                                                        fontSize: '0.75rem',
                                                      }}
                                                    >
                                                      Taxa Aplicada
                                                    </Typography>
                                                    <Typography
                                                      variant="body2"
                                                      fontWeight="medium"
                                                    >
                                                      {
                                                        transaction.metadata
                                                          .calculationDetails
                                                          .extraFontsTaxRate
                                                      }
                                                      %
                                                    </Typography>
                                                  </Box>
                                                  <Box>
                                                    <Typography
                                                      variant="body2"
                                                      color="text.secondary"
                                                      sx={{
                                                        fontSize: '0.75rem',
                                                      }}
                                                    >
                                                      Valor da Taxa
                                                    </Typography>
                                                    <Typography
                                                      variant="body2"
                                                      fontWeight="medium"
                                                      color="warning.main"
                                                    >
                                                      {
                                                        transaction.metadata
                                                          .calculationDetails
                                                          .extraFontsTaxValueFormatted
                                                      }
                                                    </Typography>
                                                  </Box>
                                                  <Box>
                                                    <Typography
                                                      variant="body2"
                                                      color="text.secondary"
                                                      sx={{
                                                        fontSize: '0.75rem',
                                                      }}
                                                    >
                                                      Fontes Extras
                                                    </Typography>
                                                    <Typography
                                                      variant="body2"
                                                      fontWeight="medium"
                                                    >
                                                      {
                                                        transaction.metadata
                                                          .calculationDetails
                                                          .extraFontsCount
                                                      }
                                                    </Typography>
                                                  </Box>
                                                </Box>
                                              </Box>
                                            )}

                                            {/* Total final */}
                                            <Box
                                              sx={{
                                                mt: 2,
                                                p: 2,
                                                backgroundColor: '#ecfdf5',
                                                borderRadius: '8px',
                                                border: '1px solid #10b981',
                                              }}
                                            >
                                              <Box
                                                sx={{
                                                  display: 'flex',
                                                  justifyContent:
                                                    'space-between',
                                                  alignItems: 'center',
                                                }}
                                              >
                                                <Typography
                                                  variant="subtitle2"
                                                  color="success.main"
                                                  fontWeight="bold"
                                                >
                                                  Total Final
                                                </Typography>
                                                <Typography
                                                  variant="h6"
                                                  color="success.main"
                                                  fontWeight="bold"
                                                >
                                                  {
                                                    transaction.metadata
                                                      .calculationDetails
                                                      .totalValueFormatted
                                                  }
                                                </Typography>
                                              </Box>
                                            </Box>
                                          </Box>
                                        ) : transaction.metadata?.details ? (
                                          <Box>
                                            {Object.entries(
                                              transaction.metadata.details
                                            ).map(([key, value]) => {
                                              const numericValue =
                                                typeof value === 'number'
                                                  ? value
                                                  : Number(value);
                                              const isNumeric =
                                                !isNaN(numericValue) &&
                                                typeof value !== 'boolean';

                                              const formatKey = (
                                                key: string
                                              ) => {
                                                return key
                                                  .replace(/([A-Z])/g, ' $1')
                                                  .replace(/^./, str =>
                                                    str.toUpperCase()
                                                  )
                                                  .replace(/_/g, ' ');
                                              };

                                              const formatValue = (
                                                val: any
                                              ) => {
                                                if (typeof val === 'boolean') {
                                                  return val ? 'Sim' : 'Não';
                                                }
                                                if (
                                                  isNumeric &&
                                                  numericValue !== 0
                                                ) {
                                                  return formatCurrency(
                                                    numericValue
                                                  );
                                                }
                                                if (
                                                  typeof val === 'string' &&
                                                  val.includes('T') &&
                                                  val.includes('Z')
                                                ) {
                                                  try {
                                                    return formatDate(
                                                      new Date(val)
                                                    );
                                                  } catch {
                                                    return val;
                                                  }
                                                }
                                                return String(val || '');
                                              };

                                              return (
                                                <Box
                                                  key={key}
                                                  sx={{
                                                    display: 'flex',
                                                    justifyContent:
                                                      'space-between',
                                                    alignItems: 'center',
                                                    py: 0.5,
                                                    borderBottom:
                                                      '1px dotted #cbd5e1',
                                                  }}
                                                >
                                                  <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                      fontStyle: 'italic',
                                                    }}
                                                  >
                                                    {formatKey(key)}
                                                  </Typography>
                                                  <Typography
                                                    variant="body2"
                                                    color="text.primary"
                                                    fontWeight="medium"
                                                  >
                                                    {formatValue(value)}
                                                  </Typography>
                                                </Box>
                                              );
                                            })}
                                          </Box>
                                        ) : (
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                          >
                                            Nenhum detalhe adicional disponível
                                            para esta transação.
                                          </Typography>
                                        )}
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            ))}

                          {/* Linha de total */}
                          <TableRow>
                            <TableCell colSpan={3}>
                              <Typography variant="subtitle1" fontWeight="bold">
                                Total da Fatura
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="subtitle1"
                                fontWeight="bold"
                                color="primary"
                              >
                                {formatCurrency(invoice.totalAmount)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </TransactionsTable>
                </TransactionsSection>
              </Box>
            ) : null}
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: '12px',
              backgroundColor: 'white',
              borderTop: '1px solid #e2e8f0',
              padding: '16px 24px',
            }}
          >
            {invoice && (
              <>
                <Box sx={{ display: 'flex', gap: '12px' }}>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleExportPdf}
                  >
                    Baixar PDF
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleExportPdf}
                  >
                    Baixar PDF Detalhado
                  </Button>
                </Box>

                {/* Botão de marcar como paga - apenas para admins */}
                {invoice.status === InvoiceStatus.CLOSED &&
                  (isAdmin ? (
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircleIcon />}
                      onClick={handleMarkAsPaid}
                    >
                      Marcar como Paga
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      startIcon={<CheckCircleIcon />}
                      disabled
                      sx={{ opacity: 0.6 }}
                      title="Apenas administradores podem marcar faturas como pagas"
                    >
                      Marcar como Paga
                    </Button>
                  ))}
              </>
            )}
          </Box>
        </Box>
      </Paper>
    </Modal>
  );
};

export default InvoiceDetailsModal;
